import { z } from "zod";

import type {
  WorkflowActionType,
  WorkflowAssignmentType,
  WorkflowCompletionPolicy,
} from "./workflow";

/**
 * İÇ ONAY AKIŞI (CLOS-158 `[151]`).
 *
 * Onay bir KARARDIR; inceleme (CLOS-155) bir görüştür. Bu ayrım şemada da korunuyor:
 * iki akışın girdileri, durumları ve kuralları birbirine karışmıyor.
 */

export const CONTRACT_APPROVAL_MODES = ["serial", "parallel"] as const;
export type ContractApprovalMode = (typeof CONTRACT_APPROVAL_MODES)[number];

export const CONTRACT_APPROVAL_MODE_NAMES: Record<ContractApprovalMode, string> = {
  serial: "Sıralı",
  parallel: "Eşzamanlı",
};

export const contractApprovalConfigSchema = z.object({
  version: z.literal(1),
  /** Desteklenen akış türleri — kod bilinmeyen bir türü açılışta reddeder. */
  modes: z.array(z.enum(CONTRACT_APPROVAL_MODES)).min(1),
  /** Tek akıştaki azami aşama ve aşama başına azami onaylayıcı. */
  maxStages: z.number().int().min(1),
  maxApproversPerStage: z.number().int().min(1),
  maxDueDays: z.number().int().min(1),
  /**
   * ⚠ Kartın *"kendi riskli işlemini TEK BAŞINA onaylayan kişi"* yasağı.
   *
   * `false` iken akışı başlatan kişi tek onaylayıcı olamıyor. Başkalarıyla BİRLİKTE
   * onaylayıcı olabilir — dört göz ilkesi sağlanmış olur. Yasağı "başlatan hiç onaylayamaz"
   * diye kursaydık, iki kişilik bir büroda onay akışı hiç kurulamazdı.
   */
  allowSelfApprovalAlone: z.boolean(),
  /** Ret gerekçesi politikası — karar kartı (`[152]`) bu alanları tüketecek. */
  rejectionRequiresNote: z.boolean(),
  decisionNoteMinLength: z.number().int().min(0),
});
export type ContractApprovalConfig = z.infer<typeof contractApprovalConfigSchema>;

const asamaSemasi = z.strictObject({
  /**
   * Aşamanın onaylayıcıları. Sıra korunuyor; aynı kişi akışın hiçbir aşamasında iki kez
   * yer alamaz (sunucu ve DB tekilliği birlikte doğruluyor).
   */
  approverUserIds: z
    .array(z.uuid("Geçerli bir kullanıcı seçilmelidir"))
    .min(1, "Her aşamada en az bir onaylayıcı olmalıdır"),
  /**
   * Aşamanın geçmesi için gereken onay sayısı. Verilmezse HERKES gerekir — sessiz bir
   * çoğunluk varsayımı, kullanıcının kurmadığı bir kuralı işletmek olurdu.
   */
  quorum: z.number().int().min(1).optional(),
});

export const startContractApprovalSchema = z.strictObject({
  versionId: z.uuid("Geçerli bir sözleşme versiyonu seçilmelidir"),
  mode: z.enum(CONTRACT_APPROVAL_MODES, { message: "Geçerli bir akış türü seçilmelidir" }),
  stages: z.array(asamaSemasi).min(1, "En az bir aşama tanımlanmalıdır"),
  /**
   * ⚠ Son tarih ZORUNLU (ADR-0065, Rapor 5 §5) — süreç ölçülebilir gecikme taşımak
   * zorunda; eski satırlar null kalır.
   */
  dueDate: z.iso.datetime({ message: "Geçerli bir son tarih girin" }),
  description: z.string().trim().max(2000, "Açıklama en fazla 2000 karakter olabilir").optional(),
});
export type StartContractApprovalInput = z.infer<typeof startContractApprovalSchema>;

export const CONTRACT_APPROVAL_STATUSES = [
  "open",
  "cancelled",
  "approved",
  "rejected",
  "changes_requested",
  /**
   * ⚠ `superseded` bir İPTAL DEĞİL (CLOS-160 `[153]`): iptal bir kullanıcı kararıdır,
   * geçersizleşme sözleşmenin yeni bir versiyonu doğduğu için sistemin vardığı sonuçtur.
   * Aynı duruma indirseydik "kim iptal etti?" sorusunun cevabı yanlış olurdu.
   */
  "superseded",
] as const;
export type ContractApprovalStatus = (typeof CONTRACT_APPROVAL_STATUSES)[number];

export const CONTRACT_APPROVAL_STATUS_NAMES: Record<ContractApprovalStatus, string> = {
  open: "Onay bekliyor",
  cancelled: "İptal edildi",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  changes_requested: "Değişiklik istendi",
  superseded: "Yeni versiyon nedeniyle geçersiz",
};

/**
 * Onaylayıcının verebileceği KARAR (CLOS-159 `[152]`).
 *
 * ⚠ `rejected` ile `changes_requested` AYRI: ret bu versiyonun onaylanmayacağını, değişiklik
 * isteği düzeltilirse onaylanabileceğini söylüyor. Üçü de TERMİNAL — karar geri alınmıyor.
 */
export const CONTRACT_APPROVAL_DECISIONS = ["approved", "rejected", "changes_requested"] as const;
export type ContractApprovalDecision = (typeof CONTRACT_APPROVAL_DECISIONS)[number];

export const decideContractApprovalTaskSchema = z.strictObject({
  decision: z.enum(CONTRACT_APPROVAL_DECISIONS, { message: "Geçerli bir karar seçilmelidir" }),
  note: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type DecideContractApprovalTaskInput = z.infer<typeof decideContractApprovalTaskSchema>;

/** ⚠ `reassigned` CLOS-233 `[170]` DEV-174 ile eklendi — `cancelled`'dan AYRI. */
export const CONTRACT_APPROVAL_TASK_STATUSES = [
  "blocked",
  "pending",
  "approved",
  "rejected",
  "changes_requested",
  "cancelled",
  "superseded",
  "reassigned",
] as const;
export type ContractApprovalTaskStatus = (typeof CONTRACT_APPROVAL_TASK_STATUSES)[number];

/** Kullanıcıya görünen adlar ŞEMADA (kriter 4). */
export const CONTRACT_APPROVAL_TASK_STATUS_NAMES: Record<ContractApprovalTaskStatus, string> = {
  blocked: "Sırası gelmedi",
  pending: "Bekliyor",
  approved: "Onayladı",
  rejected: "Reddetti",
  changes_requested: "Değişiklik istedi",
  cancelled: "İptal edildi",
  superseded: "Yeni versiyon nedeniyle geçersiz",
  reassigned: "Başka bir onaylayıcıya devredildi",
};

export type ContractApprovalTaskSummary = {
  id: string;
  approverUserId: string;
  approverName: string;
  position: number;
  status: ContractApprovalTaskStatus;
  decisionNote: string | null;
  resolvedAt: string | null;
  /** `reassigned` durumundaki görevde dolu (CLOS-922/CLOS-924). */
  reassignedToTaskId: string | null;
};

/** Görev iptali (CLOS-233 `[170]` DEV-174 — CLOS-922 ortak çekirdek/CLOS-924 adapter). */
export const cancelContractApprovalTaskSchema = z.strictObject({
  reason: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type CancelContractApprovalTaskInput = z.infer<typeof cancelContractApprovalTaskSchema>;

export const reassignContractApprovalTaskSchema = z.strictObject({
  newApproverUserId: z.uuid("Geçerli bir kullanıcı seçilmelidir"),
  reason: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type ReassignContractApprovalTaskInput = z.infer<typeof reassignContractApprovalTaskSchema>;

export type ContractApprovalStageSummary = {
  id: string;
  position: number;
  quorum: number;
  status:
    | "waiting"
    | "active"
    | "approved"
    | "rejected"
    | "changes_requested"
    | "cancelled"
    | "superseded";
  /**
   * ⚠ Dinamik iş akışından doğan aşamanın ANLIK GÖRÜNTÜSÜ; elle kurulan akışta `null`.
   *
   * Tanıma bakan bir okuma olsaydı, tanıma yeni sürüm yayınlamak çalışan akışın ekranını
   * geçmişe dönük değiştirirdi. Boşluk "bilinmiyor" değil, "bu akışın bir tanımı yok"
   * demek — tahminle doldurulmaz (Karar Eki §58).
   */
  name: string | null;
  instruction: string | null;
  actionType: WorkflowActionType | null;
  assignmentType: WorkflowAssignmentType | null;
  completionPolicy: WorkflowCompletionPolicy | null;
  /**
   * ⚠ Aşama neden ilerleyemiyor — rol havuzu çözülemediğinde dolu (Kart 4).
   *
   * Boş havuzu sessizce geçmek akışı kimse karar vermeden ilerletirdi; sebebi yazmamak ise
   * kullanıcıyı "neden bekliyorum?" sorusuyla baş başa bırakırdı.
   */
  blockedReason: string | null;
  tasks: ContractApprovalTaskSummary[];
};

export type ContractApprovalSummary = {
  id: string;
  contractId: string;
  versionId: string;
  versionNumber: number;
  mode: ContractApprovalMode;
  status: ContractApprovalStatus;
  dueDate: string | null;
  description: string | null;
  startedByName: string;
  createdAt: string;
  stages: ContractApprovalStageSummary[];
};
